import { useState, useCallback, useMemo } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { DownloadSimple, UploadSimple, CheckCircle, XCircle, ArrowClockwise } from "@phosphor-icons/react";

const TEMPLATES = [
    {
        id: "employees",
        title: "Employees",
        blurb: "Onboard multiple employees at once — 28 columns mirror the New Employee popup 1-to-1 (identity, salary, PF/TDS/PT, bank, commission, emergency contact). Employee code is auto-generated. name / email / phone / aadhaar / tier / base_salary are mandatory.",
        template_url: "/bulk-import/employees/template",
        preview_url: "/bulk-import/employees/preview",
        commit_url: "/bulk-import/employees/commit",
        columns: [
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "tier", label: "Tier" },
            { key: "department", label: "Dept" },
            { key: "location", label: "Location" },
            { key: "base_salary", label: "Base ₹", align: "right" },
            { key: "pf_employee_amount", label: "PF-Emp ₹", align: "right" },
            { key: "tds_amount", label: "TDS ₹", align: "right" },
            { key: "bank_account_number", label: "A/C" },
            { key: "ifsc_code", label: "IFSC" },
            { key: "manager_email", label: "Manager" },
        ],
        actionCol: null,
    },
    {
        id: "attendance",
        title: "Employee Attendance",
        blurb: "Manual monthly attendance for labour employees. Legend — P: Present · HL: Half day (5 hrs = 0.5 day) · LV: Leave (paid) · A: Absent (LOP) · OT: overtime hours.",
        template_url: "/bulk-import/attendance/template",
        preview_url: "/bulk-import/attendance/preview",
        commit_url: "/bulk-import/attendance/commit",
        columns: [
            { key: "employee_code", label: "Emp code" },
            { key: "employee_name", label: "Employee" },
            { key: "month", label: "Month" },
            { key: "present", label: "P", align: "right" },
            { key: "half_day", label: "HL", align: "right" },
            { key: "leave", label: "LV", align: "right" },
            { key: "absent", label: "A", align: "right" },
            { key: "ot_hours", label: "OT", align: "right" },
        ],
        actionCol: "action",
    },
    {
        id: "payroll",
        title: "Payroll Run Submission",
        blurb: "Bulk-create draft payroll runs. base_pay is shown from the employee master for reference; not editable via this template. Existing runs are flagged as duplicates.",
        template_url: "/bulk-import/payroll/template",
        preview_url: "/bulk-import/payroll/preview",
        commit_url: "/bulk-import/payroll/commit",
        columns: [
            { key: "employee_code", label: "Emp code" },
            { key: "employee_name", label: "Employee" },
            { key: "month", label: "Month" },
            { key: "base_pay", label: "Base ₹", align: "right" },
            { key: "ot_hours", label: "OT hrs", align: "right" },
            { key: "incentive", label: "Incentive ₹", align: "right" },
            { key: "penalty", label: "Penalty ₹", align: "right" },
        ],
        actionCol: "action",
    },
    {
        id: "contract_workers",
        title: "Contract Workers",
        blurb: "Onboard contract / piece-rate workers in bulk. name and entity_type are mandatory. entity_type is 'individual' (TDS @ 1%) or 'company' (TDS @ 2%) — HUF / firm / partnership auto-map to company. Worker code auto-generates as CW-### if left blank. Piece rates per product are learned from the monthly Chart of Work sheet — not this template.",
        template_url: "/bulk-import/contract-workers/template",
        preview_url: "/bulk-import/contract-workers/preview",
        commit_url: "/bulk-import/contract-workers/commit",
        columns: [
            { key: "worker_code", label: "Code" },
            { key: "name", label: "Name" },
            { key: "entity_type", label: "Entity" },
            { key: "pan", label: "PAN" },
            { key: "gstin", label: "GSTIN" },
            { key: "phone", label: "Phone" },
            { key: "bank_account_number", label: "A/C" },
            { key: "ifsc_code", label: "IFSC" },
        ],
        actionCol: "action",
    },
];


export default function BulkImports() {
    const [activeId, setActiveId] = useState("employees");
    const active = useMemo(() => TEMPLATES.find((t) => t.id === activeId), [activeId]);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const downloadTemplate = useCallback(async () => {
        try {
            const url = `${API}${active.template_url}`;
            window.location.href = url;
        } catch (e) { toast.error(formatApiError(e)); }
    }, [active]);

    const runPreview = useCallback(async () => {
        if (!file) return toast.error("Choose an xlsx file first");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(active.preview_url, fd,
                { headers: { "Content-Type": "multipart/form-data" }});
            setPreview(data);
            const okCount = data.summary?.ok ?? data.summary?.add ?? data.summary?.create ?? 0;
            const errCount = data.summary?.errors ?? 0;
            toast.success(`Parsed ${data.summary?.total || 0} rows · ${okCount} ready · ${errCount} with errors`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }, [file, active]);

    const commit = useCallback(async () => {
        if (!preview) return;
        setBusy(true);
        try {
            const { data } = await api.post(active.commit_url, { rows: preview.rows });
            const created = (data.created || data.added || []).length;
            const corrected = (data.corrected || []).length;
            const skipped = (data.skipped || []).length;
            toast.success(
                `Committed — added ${created}${corrected ? ` · corrected ${corrected}` : ""}${skipped ? ` · skipped ${skipped}` : ""}`
            );
            setPreview(null); setFile(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }, [preview, active]);

    const reset = () => { setPreview(null); setFile(null); };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Bulk imports"
                sub="Download a template, fill it in, upload for review, and confirm to save. Every import runs a validation pass before any data goes live." />

            <div className="p-8 space-y-6">
                {/* Tabs */}
                <div className="flex border-b" style={{ borderColor: "var(--se-border)" }}>
                    {TEMPLATES.map((t) => (
                        <button key={t.id} onClick={() => { setActiveId(t.id); reset(); }}
                                className={`px-5 py-2.5 text-sm border-b-2 -mb-px ${activeId === t.id ? "border-orange-500 text-orange-600 font-medium" : "border-transparent text-stone-600 hover:text-stone-900"}`}
                                data-testid={`bi-tab-${t.id}`}>
                            {t.title}
                        </button>
                    ))}
                </div>

                <div className="bg-white border rounded-md p-6 space-y-4" style={{ borderColor: "var(--se-border)" }}>
                    <div className="text-sm text-stone-600">{active.blurb}</div>

                    <div className="flex items-center gap-3 flex-wrap">
                        <Button variant="outline" onClick={downloadTemplate} data-testid={`bi-${activeId}-download`}>
                            <DownloadSimple size={14} className="mr-2" /> Download {active.title.toLowerCase()} template
                        </Button>
                        <div className="text-[11px] text-stone-500">Then fill it in and upload below.</div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap pt-2 border-t" style={{ borderColor: "var(--se-border)" }}>
                        <Label>Upload xlsx</Label>
                        <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                               className="max-w-md" data-testid={`bi-${activeId}-file`} />
                        <Button onClick={runPreview} disabled={busy || !file} data-testid={`bi-${activeId}-preview-btn`}>
                            <UploadSimple size={14} className="mr-2" /> Preview
                        </Button>
                        {preview ? (
                            <Button variant="outline" onClick={reset} data-testid={`bi-${activeId}-reset`}>
                                <ArrowClockwise size={14} className="mr-1" /> Reset
                            </Button>
                        ) : null}
                    </div>
                </div>

                {preview ? (
                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 border-b flex items-center gap-4" style={{ borderColor: "var(--se-border)" }}>
                            <div className="font-serif text-lg mr-auto">Preview — {preview.rows.length} rows</div>
                            <SummaryBadges summary={preview.summary} />
                            <Button onClick={commit} disabled={busy || !preview.rows.some((r) => !r.errors.length)}
                                    style={{ background: "#16a34a", color: "#fff" }} data-testid={`bi-${activeId}-commit`}>
                                <CheckCircle size={14} className="mr-2" /> Confirm & save
                            </Button>
                        </div>
                        <div className="max-h-[540px] overflow-auto">
                            <table className="se-table" data-testid={`bi-${activeId}-preview-table`}>
                                <thead>
                                    <tr>
                                        <th className="w-8">#</th>
                                        {active.columns.map((c) => (
                                            <th key={c.key} className={c.align === "right" ? "text-right" : ""}>{c.label}</th>
                                        ))}
                                        {active.actionCol ? <th>Action</th> : null}
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.map((r) => {
                                        const hasErrors = (r.errors || []).length > 0;
                                        const cls = hasErrors ? "bg-rose-50" :
                                                    r.action === "duplicate" ? "bg-stone-50" :
                                                    r.action === "locked" ? "bg-amber-50" :
                                                    r.action === "correct" ? "bg-sky-50" : "";
                                        return (
                                            <tr key={r.row_index} className={cls}>
                                                <td>{r.row_index}</td>
                                                {active.columns.map((c) => (
                                                    <td key={c.key} className={c.align === "right" ? "se-num text-right" : ""}>
                                                        {r[c.key] ?? "—"}
                                                    </td>
                                                ))}
                                                {active.actionCol ? <td className="uppercase text-xs">{r[active.actionCol] || "—"}</td> : null}
                                                <td className="text-xs">
                                                    {hasErrors ? (
                                                        <span className="text-rose-700">{r.errors.join(", ")}</span>
                                                    ) : r.warnings?.length ? (
                                                        <span className="text-amber-700">{r.warnings.join(", ")}</span>
                                                    ) : (
                                                        <span className="text-emerald-700">Ready</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
}

function SummaryBadges({ summary }) {
    if (!summary) return null;
    const entries = Object.entries(summary).filter(([k, v]) => k !== "total" && v > 0);
    return (
        <div className="flex gap-2 text-xs">
            {entries.map(([k, v]) => (
                <span key={k} className={`px-2 py-1 rounded ${
                    k === "errors" ? "bg-rose-100 text-rose-700" :
                    k === "locked" ? "bg-amber-100 text-amber-700" :
                    k === "duplicate" || k === "duplicates" ? "bg-stone-100 text-stone-700" :
                    k === "correct" ? "bg-sky-100 text-sky-700" :
                    "bg-emerald-100 text-emerald-700"
                }`}>
                    {k}: {v}
                </span>
            ))}
        </div>
    );
}
